import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Milestone, Phase } from '../types';

const FIELDS = [
  'dagrun_pm_milestoneid','dagrun_pm_name','dagrun_pm_duedate','dagrun_pm_completeddate',
  'dagrun_pm_status','dagrun_pm_completioncriteria','dagrun_pm_isclientvisible',
  'dagrun_pm_ownerid','dagrun_pm_ownername',
  '_dagrun_pm_projectid_value','_dagrun_pm_phaseid_value',
];

export async function getProjectMilestones(pca: IPublicClientApplication, projectId: string): Promise<Milestone[]> {
  return dvList<Milestone>(pca, 'dagrun_pm_milestones', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_duedate asc',
  });
}

export async function createMilestone(pca: IPublicClientApplication, projectId: string, name: string): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_milestones', {
    dagrun_pm_name: name,
    dagrun_pm_status: 100000000,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  });
}

export async function updateMilestone(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_milestones', id, patch);
}

export async function deleteMilestone(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_milestones', id);
}

export async function createMilestoneWithFields(
  pca: IPublicClientApplication,
  projectId: string,
  fields: {
    name: string; duedate?: string | null; phaseid?: string | null;
    completioncriteria?: string | null; isclientvisible?: boolean;
    ownerid?: string | null; ownername?: string | null;
  },
): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: fields.name,
    dagrun_pm_status: 100000000,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (fields.duedate) body.dagrun_pm_duedate = fields.duedate;
  if (fields.completioncriteria) body.dagrun_pm_completioncriteria = fields.completioncriteria;
  if (fields.isclientvisible != null) body.dagrun_pm_isclientvisible = fields.isclientvisible;
  if (fields.phaseid) body['dagrun_pm_phaseid@odata.bind'] = `/dagrun_pm_phases(${fields.phaseid})`;
  if (fields.ownerid) body.dagrun_pm_ownerid = fields.ownerid;
  if (fields.ownername) body.dagrun_pm_ownername = fields.ownername;
  return dvCreate(pca, 'dagrun_pm_milestones', body);
}

// Fetch every milestone across the tenant in a single query. Used by the
// Portfolio Gantt view. Guarded with a top cap so a runaway multi-project
// tenant doesn't explode the response.
export async function getAllMilestones(pca: IPublicClientApplication): Promise<Milestone[]> {
  return dvList<Milestone>(pca, 'dagrun_pm_milestones', {
    select: FIELDS,
    orderby: 'dagrun_pm_duedate asc',
    top: 2000,
  });
}

export async function getProjectPhases(pca: IPublicClientApplication, projectId: string): Promise<Phase[]> {
  return dvList<Phase>(pca, 'dagrun_pm_phases', {
    select: ['dagrun_pm_phaseid', 'dagrun_pm_name', 'dagrun_pm_sortorder'],
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'dagrun_pm_sortorder asc',
  });
}
